import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Spinner, Alert } from 'react-bootstrap';
import FilterComponent, { FilterOption, AppliedFilter, DropdownOption } from '../../components/common/FilterComponent';
import PaginationComponent from '../../components/common/PaginationComponent';
import './ConformidadePage.css';
import { useHistory } from 'react-router-dom';
import { useEntity } from '../../contexts/EntityContext';
import { entityService, ConformidadePageResponse, ConformidadeAction, ConformidadeFilters } from '../../services/entityService';
import { actionService, AcaoHistoricoResponse } from '../../services/actionService';
import { areaService, Area } from '../../services/areaService';

const ConformidadePage: React.FC = () => {
    const history = useHistory();
    const { currentEntity } = useEntity();

    // State for data
    const [actions, setActions] = useState<ConformidadeAction[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // State for modal history (fetched on-demand)
    const [modalHistory, setModalHistory] = useState<AcaoHistoricoResponse[]>([]);
    const [modalHistoryLoading, setModalHistoryLoading] = useState(false);
    const [modalHistoryError, setModalHistoryError] = useState<string | null>(null);

    // State for pagination
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // State for filters
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);
    const [showFilter, setShowFilter] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [filterType, setFilterType] = useState<'ano' | 'areaId' | 'status' | 'responsavelId' | 'item' | 'titulo' | 'questao'>('ano');
    const [sortAsc, setSortAsc] = useState(false);
    const [selectedAction, setSelectedAction] = useState<ConformidadeAction | null>(null);
    const [showModal, setShowModal] = useState(false);

    // State for dropdown data
    const [areas, setAreas] = useState<Area[]>([]);
    const [areasLoading, setAreasLoading] = useState(false);

    // Derive unique responsibles with UUIDs from the current page results
    const responsaveisDisponiveis = actions
        .filter(a => a.responsible_id && a.responsible)
        .reduce<{ value: string; label: string }[]>((acc, a) => {
            if (!acc.find(r => r.value === a.responsible_id)) {
                acc.push({ value: a.responsible_id!, label: a.responsible });
            }
            return acc;
        }, []);

    const filterOptions: FilterOption[] = [
        { value: 'ano', label: 'Ano' },
        { value: 'status', label: 'Status' },
        { value: 'areaId', label: 'Área' },
        ...(responsaveisDisponiveis.length > 0 ? [{ value: 'responsavelId', label: 'Responsável' }] : []),
        { value: 'questao', label: 'Questão' },
        { value: 'item', label: 'Item' },
        { value: 'titulo', label: 'Ação' },
    ];

    // Helper function to get status badge color
    const getStatusBadgeColor = (status: string): string => {
        switch (status) {
            case 'CONCLUIDA':
                return 'bg-success';
            case 'EM_ANDAMENTO':
                return 'bg-warning';
            case 'AGUARDANDO_VALIDACAO':
                return 'bg-info';
            case 'PENDENTE':
                return 'bg-danger';
            default:
                return 'bg-secondary';
        }
    };

    // Fetch areas for dropdown
    useEffect(() => {
        const fetchAreas = async () => {
            if (!currentEntity) return;
            try {
                setAreasLoading(true);
                const areasData = await areaService.getAvailableAreas(currentEntity.entidade_id);
                setAreas(areasData);
            } catch (err) {
                console.error('Error fetching areas:', err);
            } finally {
                setAreasLoading(false);
            }
        };

        fetchAreas();
    }, [currentEntity]);

    // Fetch data for the current entity with pagination and filters
    useEffect(() => {
        const fetchConformidadeData = async () => {
            if (!currentEntity) {
                setError('Entidade não selecionada');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // Build filters object from applied filters
                const currentFilters: ConformidadeFilters = {};
                appliedFilters.forEach(filter => {
                    switch (filter.type) {
                        case 'ano':
                            currentFilters.ano = parseInt(filter.value);
                            break;
                        case 'status':
                            currentFilters.status = filter.value;
                            break;
                        case 'areaId':
                            currentFilters.areaId = filter.value;
                            break;
                        case 'responsavelId':
                            currentFilters.responsavelId = filter.value;
                            break;
                        case 'questao':
                            currentFilters.questao = filter.value;
                            break;
                        case 'item':
                            currentFilters.item = filter.value;
                            break;
                        case 'titulo':
                            currentFilters.titulo = filter.value;
                            break;
                    }
                });

                // Fetch paginated conformidade data from the backend
                const response: ConformidadePageResponse = await entityService.getConformidadeData(
                    currentEntity.entidade_id,
                    currentPage,
                    pageSize,
                    currentFilters
                );

                // Set actions and pagination data from the response
                setActions(response.content);
                setTotalElements(response.totalElements);
                setTotalPages(response.totalPages);

            } catch (err) {
                setError('Erro ao carregar dados de conformidade. Por favor, tente novamente.');
                console.error('Error fetching conformidade data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchConformidadeData();
    }, [currentEntity, currentPage, pageSize, sortAsc, appliedFilters]);

    // Actions are already filtered and sorted by the backend
    const sortedActions = actions;

    // Modal handlers
    const handleShowHistory = async (action: ConformidadeAction) => {
        setSelectedAction(action);
        setShowModal(true);
        setModalHistory([]);
        setModalHistoryError(null);
        try {
            setModalHistoryLoading(true);
            const data = await actionService.getActionHistory(action.acao_id);
            setModalHistory(data);
        } catch (err) {
            console.error('Error fetching action history:', err);
            setModalHistoryError('Erro ao carregar histórico.');
        } finally {
            setModalHistoryLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedAction(null);
        setModalHistory([]);
        setModalHistoryError(null);
    };

    const handleAnexoClick = async (anexoId: string) => {
        try {
            const downloadResponse = await actionService.getAnexoDownloadUrl(anexoId);
            window.open(downloadResponse.download_url, '_blank');
        } catch (error) {
            console.error('Error getting download URL:', error);
            alert('Erro ao baixar arquivo. Por favor, tente novamente.');
        }
    };



    // Helper function to get dropdown options based on filter type
    const getDropdownOptions = (): DropdownOption[] => {
        if (filterType === 'areaId') {
            return areas.map(area => ({
                value: area.area_id,
                label: area.nome
            }));
        } else if (filterType === 'responsavelId') {
            return responsaveisDisponiveis;
        } else if (filterType === 'status') {
            return [
                { value: 'EM_ANDAMENTO', label: 'Em Andamento' },
                { value: 'CONCLUIDA', label: 'Concluída' },
                { value: 'AGUARDANDO_VALIDACAO', label: 'Aguardando Validação' },
                { value: 'PENDENTE', label: 'Pendente' },
            ];
        }
        return [];
    };

    // Helper function to convert status to user-friendly display text
    const getStatusDisplayText = (status: string): string => {
        switch (status) {
            case 'EM_ANDAMENTO':
                return 'Em Andamento';
            case 'CONCLUIDA':
                return 'Concluída';
            case 'PENDENTE':
                return 'Pendente';
            case 'AGUARDANDO_VALIDACAO':
                return 'Aguardando Validação';
            default:
                return status; // Return original if no mapping found
        }
    };

    // Function to apply filters (called when user clicks search or presses Enter)
    const handleFilterApply = (filterText: string, filterType: string) => {
        const typedFilterType = filterType as 'ano' | 'areaId' | 'status' | 'responsavelId';

        // Only apply if filter text is not empty
        if (!filterText.trim()) return;

        let filterValue = filterText.trim();
        let displayValue = filterText.trim();

        // For areaId and responsavelId, get the display value from the dropdown options
        if (typedFilterType === 'areaId') {
            const area = areas.find(a => a.area_id === filterValue);
            displayValue = area?.nome || filterText.trim();
        } else if (typedFilterType === 'responsavelId') {
            const responsavel = responsaveisDisponiveis.find(r => r.value === filterValue);
            displayValue = responsavel?.label || filterText.trim();
        } else if (typedFilterType === 'status') {
            // For status, the filterValue is already the backend value (e.g., 'EM_ANDAMENTO')
            // and we need to get the display value from our mapping
            displayValue = getStatusDisplayText(filterValue);
        }

        // Add to applied filters
        const filterOption = filterOptions.find(opt => opt.value === typedFilterType);
        const newAppliedFilter: AppliedFilter = {
            type: typedFilterType,
            value: filterValue,
            label: filterOption?.label || typedFilterType,
            displayValue: displayValue
        };

        // Remove existing filter of same type if exists
        const updatedFilters = appliedFilters.filter(f => f.type !== typedFilterType);
        setAppliedFilters([...updatedFilters, newAppliedFilter]);

        // Clear the input after applying
        setFilterText('');
        setError(null); // Clear any previous errors
    };

    // Function to remove a specific filter
    const handleFilterRemove = (filterType: string) => {
        const updatedFilters = appliedFilters.filter(f => f.type !== filterType);
        setAppliedFilters(updatedFilters);
    };

    // Function to clear filters
    const handleFilterClear = () => {
        setFilterText('');
        setAppliedFilters([]);
    };

    // Pagination handlers
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(0); // Reset to first page when changing page size
    };

    if (loading) {
        return (
            <div className="container-fluid p-4">
                <Button
                    variant="outline-secondary"
                    onClick={() => history.goBack()}
                    className="return-button mb-3 d-flex align-items-center gap-2"
                >
                    <span>←</span> Voltar
                </Button>
                <div className="text-center p-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </Spinner>
                    <p className="mt-3 text-muted">Carregando dados de conformidade...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid p-4">
                <Button
                    variant="outline-secondary"
                    onClick={() => history.goBack()}
                    className="return-button mb-3 d-flex align-items-center gap-2"
                >
                    <span>←</span> Voltar
                </Button>
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            </div>
        );
    }

    return (
        <div className="container-fluid p-4">
            <Button
                variant="outline-secondary"
                onClick={() => history.goBack()}
                className="return-button mb-3 d-flex align-items-center gap-2"
            >
                <span>←</span> Voltar
            </Button>
            <h2 className="mb-4">Conformidade de Ações</h2>
            {!showFilter && (
                <div className="mb-2">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowFilter(true)}
                        className="filter-toggle-button d-inline-flex align-items-center gap-2"
                        aria-expanded={showFilter}
                    >
                        <span role="img" aria-label="filtrar">🔍</span>
                        Filtrar
                    </Button>
                </div>
            )}
            {showFilter && (
                <div className="mb-3">
                    <FilterComponent
                        showFilter={showFilter}
                        filterText={filterText}
                        filterType={filterType}
                        filterOptions={filterOptions}
                        appliedFilters={appliedFilters}
                        dropdownOptions={getDropdownOptions()}
                        onFilterApply={handleFilterApply}
                        onFilterTypeChange={(type) => setFilterType(type as 'ano' | 'areaId' | 'status' | 'responsavelId' | 'item' | 'titulo' | 'questao')}
                        onFilterTextChange={setFilterText}
                        onFilterTextClear={handleFilterClear}
                        onFilterToggle={() => setShowFilter(false)}
                        onFilterRemove={handleFilterRemove}
                        placeholderText={
                            filterType === 'ano' ? 'Buscar por ano...' :
                            filterType === 'status' ? 'Buscar por status...' :
                            filterType === 'areaId' ? 'Buscar por área...' :
                            filterType === 'responsavelId' ? 'Buscar por responsável...' :
                            filterType === 'questao' ? 'Buscar por questão...' :
                            filterType === 'item' ? 'Buscar por item...' :
                            'Buscar por ação...'
                        }
                    />

                    {/* Help text for area and responsible filters */}
                    {(filterType === 'areaId' || filterType === 'responsavelId') && (
                        <div className="mt-2">
                            <small className="text-muted">
                                {filterType === 'areaId' && areasLoading ? 'Carregando áreas...' :
                                    filterType === 'areaId' ? `Selecione uma área (${areas.length} áreas disponíveis)` :
                                        `Selecione um responsável (${responsaveisDisponiveis.length} disponíveis nesta página)`}
                            </small>
                        </div>
                    )}
                </div>
            )}


            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Ano</th>
                            <th>Área</th>
                            <th>Questão</th>
                            <th>Item</th>
                            <th>Ação</th>
                            <th>Responsável</th>
                            <th>Status</th>
                            <th style={{ cursor: 'pointer' }} onClick={() => setSortAsc(s => !s)}>
                                Score <i className={`mdi mdi-arrow-${sortAsc ? 'up' : 'down'}-bold`}></i>
                            </th>
                            <th>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedActions.map(action => (
                            <tr key={action.acao_id}>
                                <td>{action.year}</td>
                                <td>{action.area}</td>
                                <td>{action.question}</td>
                                <td>{action.item}</td>
                                <td>{action.titulo}</td>
                                <td>{action.responsible}</td>
                                <td>
                                    <span className={`badge ${getStatusBadgeColor(action.status)}`}>
                                        {getStatusDisplayText(action.status)}
                                    </span>
                                </td>
                                <td>{action.score}</td>
                                <td>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleShowHistory(action)}
                                        className="d-flex align-items-center border border-secondary text-secondary"
                                    >
                                        <i className="mdi mdi-history mr-1 text-secondary"></i>
                                        <span className="text-secondary">Histórico</span>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <PaginationComponent
                    page={currentPage}
                    size={pageSize}
                    total={totalElements}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    entityLabel="ações"
                />
            )}

            {/* Action History Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg" centered
                dialogClassName="fixed-history-modal"
            >
                <Modal.Header>
                    <Modal.Title>
                        <div>
                            <h5 className="mb-2">Histórico da Ação</h5>
                            <h6 className="text-muted mb-1">{selectedAction?.titulo}</h6>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {selectedAction && (
                        <>
                            {/* Action General Information */}
                            <div className="action-info mb-4">
                                <h6 className="text-primary mb-3">Informações Gerais da Ação</h6>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="info-item mb-2">
                                            <strong>Ano:</strong> {selectedAction.year}
                                        </div>
                                        <div className="info-item mb-2">
                                            <strong>Área:</strong> {selectedAction.area}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="info-item mb-2">
                                            <strong>Questão:</strong> {selectedAction.question}
                                        </div>
                                        <div className="info-item mb-2">
                                            <strong>Item:</strong> {selectedAction.item}
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-6">
                                        <div className="info-item mb-2">
                                            <strong>Responsável:</strong> {selectedAction.responsible}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="info-item mb-2">
                                            <strong>Status Atual:</strong>
                                            <span className={`badge ms-2 ${getStatusBadgeColor(selectedAction.status)}`}>
                                                {getStatusDisplayText(selectedAction.status)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-12">
                                        <div className="info-item mb-2">
                                            <strong>Descrição:</strong> {selectedAction.descricao}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* History Section */}
                            <hr className="my-4" />
                            <h6 className="text-primary mb-3">Histórico de Comentários</h6>
                            {modalHistoryLoading ? (
                                <div className="text-center text-muted py-3">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Carregando histórico...
                                </div>
                            ) : modalHistoryError ? (
                                <div className="text-danger py-2">{modalHistoryError}</div>
                            ) : modalHistory.length === 0 ? (
                                <div className="text-center text-muted py-3">Nenhum histórico disponível.</div>
                            ) : (
                                <div className="mt-3">
                                    {modalHistory.map((h: AcaoHistoricoResponse) => (
                                        <div key={h.acaoHistoricoId} className="history-item mb-3 p-3 border rounded">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div>
                                                    {h.type && (
                                                        <span className={`badge me-2 ${getStatusBadgeColor(h.type)}`}>
                                                            {getStatusDisplayText(h.type)}
                                                        </span>
                                                    )}
                                                    <small className="text-muted">
                                                        {new Date(h.dtCriacao).toLocaleDateString('pt-BR')} às{' '}
                                                        {new Date(h.dtCriacao).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                    </small>
                                                </div>
                                                <small className="text-muted">
                                                    Por: <strong>{h.criadoPorNome}</strong>
                                                </small>
                                            </div>
                                            {h.comentario && (
                                                <div className="comment-content mt-2 p-2 bg-light rounded">
                                                    <i className="mdi mdi-comment-text me-2 text-muted"></i>
                                                    {h.comentario}
                                                </div>
                                            )}
                                            {h.anexos?.length > 0 && (
                                                <div className="attachment mt-2">
                                                    <i className="mdi mdi-paperclip me-2 text-muted"></i>
                                                    <span className="text-muted">Anexos: </span>
                                                    {h.anexos.map((anexo) => (
                                                        <small
                                                            key={anexo.anexoId}
                                                            className="text-primary d-block ms-4"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => handleAnexoClick(anexo.anexoId)}
                                                        >
                                                            {anexo.anexo}
                                                        </small>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ConformidadePage;
